// GiftReceiptTemplate.jsx
// IRS-compliant gift receipt / acknowledgment letter.
// Renders as styled HTML -- designed for browser print / Save as PDF.
// Reads all chapter identity fields from chapterSettings (AppContext).
// Conditional rendering by gift type: cash, inkind, stock, daf.
// Path: src/components/features/GiftReceiptTemplate.jsx
//
// v1.2.0 — Custom receipt language from chapterSettings with token
//          replacement. Blank fields fall back to hardcoded defaults.
// v1.1.0 — Fixed field names to match ChapterSettingsPanel state keys.

import React from "react";
import { formatCurrency } from "../../constants/giftTypes";
import { replaceTokens, buildReceiptContext } from "../../utils/receiptTokens";

// ── Print styles injected once when component mounts ─────────
const PRINT_STYLES = `
  @media print {
    body * { visibility: hidden; }
    .gift-receipt-printable, .gift-receipt-printable * { visibility: visible; }
    .gift-receipt-printable { position: absolute; left: 0; top: 0; width: 100%; }
    .gift-receipt-no-print { display: none !important; }
  }
`;

// ── Shared style constants ────────────────────────────────────
const FONT = "Georgia, 'Times New Roman', serif";
const FONT_SANS = "Arial, Helvetica, sans-serif";

const styles = {
  page: {
    fontFamily: FONT,
    maxWidth: 680,
    margin: "0 auto",
    padding: "48px 56px",
    background: "#ffffff",
    color: "#1a1a1a",
    fontSize: 14,
    lineHeight: 1.6,
  },
  header: {
    borderBottom: "2px solid #1a1a1a",
    paddingBottom: 20,
    marginBottom: 24,
    display: "flex",
    alignItems: "flex-start",
    gap: 20,
  },
  logoBlock: {
    flex: "0 0 auto",
  },
  logo: {
    width: 72,
    height: 72,
    objectFit: "contain",
  },
  orgBlock: {
    flex: 1,
  },
  orgName: {
    fontFamily: FONT_SANS,
    fontSize: 18,
    fontWeight: 700,
    color: "#1a1a1a",
    marginBottom: 4,
    lineHeight: 1.2,
  },
  orgMeta: {
    fontFamily: FONT_SANS,
    fontSize: 11,
    color: "#555555",
    lineHeight: 1.5,
  },
  receiptBadge: {
    flex: "0 0 auto",
    textAlign: "right",
  },
  receiptLabel: {
    fontFamily: FONT_SANS,
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "#888888",
    marginBottom: 2,
  },
  receiptNumber: {
    fontFamily: FONT_SANS,
    fontSize: 15,
    fontWeight: 700,
    color: "#1a1a1a",
  },
  receiptDate: {
    fontFamily: FONT_SANS,
    fontSize: 11,
    color: "#555555",
    marginTop: 2,
  },
  salutation: {
    marginBottom: 20,
    fontSize: 14,
  },
  donorName: {
    fontWeight: 700,
  },
  section: {
    background: "#f8f8f8",
    border: "1px solid #dddddd",
    borderRadius: 4,
    padding: "16px 20px",
    marginBottom: 20,
  },
  sectionTitle: {
    fontFamily: FONT_SANS,
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "#888888",
    marginBottom: 10,
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 6,
    fontSize: 13,
  },
  rowLabel: {
    color: "#555555",
    flex: "0 0 auto",
    paddingRight: 16,
  },
  rowValue: {
    fontWeight: 600,
    textAlign: "right",
    flex: 1,
  },
  divider: {
    borderTop: "1px solid #dddddd",
    margin: "10px 0",
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    fontSize: 14,
    fontWeight: 700,
    marginTop: 4,
  },
  irsBlock: {
    border: "1px solid #cccccc",
    borderLeft: "4px solid #1a1a1a",
    padding: "14px 18px",
    marginBottom: 20,
    fontSize: 13,
    color: "#333333",
    lineHeight: 1.7,
    fontStyle: "italic",
  },
  signatureBlock: {
    marginTop: 32,
    paddingTop: 20,
    borderTop: "1px solid #dddddd",
  },
  signatureImg: {
    height: 56,
    maxWidth: 200,
    objectFit: "contain",
    display: "block",
    marginBottom: 4,
  },
  signatureLine: {
    borderTop: "1px solid #555555",
    width: 200,
    marginBottom: 4,
  },
  signatureName: {
    fontFamily: FONT_SANS,
    fontSize: 13,
    fontWeight: 700,
    color: "#1a1a1a",
  },
  signatureTitle: {
    fontFamily: FONT_SANS,
    fontSize: 12,
    color: "#555555",
  },
  footer: {
    marginTop: 32,
    paddingTop: 12,
    borderTop: "1px solid #dddddd",
    fontFamily: FONT_SANS,
    fontSize: 10,
    color: "#888888",
    lineHeight: 1.5,
    textAlign: "center",
  },
  dafNote: {
    background: "#fffbeb",
    border: "1px solid #f59e0b",
    borderRadius: 4,
    padding: "10px 14px",
    marginBottom: 16,
    fontFamily: FONT_SANS,
    fontSize: 12,
    color: "#92400e",
  },
};

// ── Date formatter ────────────────────────────────────────────
const fmtDate = function (dateStr) {
  if (!dateStr) return "--";
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

// ── IRS language by gift type (NOT editable by admins) ────────
const getIrsLanguage = function (gift, orgName) {
  var org = orgName || "this organization";

  if (gift.giftType === "inkind") {
    return (
      "No goods or services were provided in exchange for this contribution. " +
      org +
      " did not assign a value to this gift. " +
      "Please consult your tax advisor to determine the fair market value for deduction purposes."
    );
  }

  if (gift.giftType === "stock") {
    return (
      "No goods or services were provided in exchange for this contribution. " +
      "No value has been assigned by " +
      org +
      ". " +
      "The deductible amount is the fair market value of the securities on the date of transfer, " +
      "as determined by the donor."
    );
  }

  if (gift.giftType === "daf") {
    return (
      "This letter acknowledges receipt of a grant from " +
      (gift.dafSponsoringOrg || "a donor advised fund") +
      " on behalf of a donor. " +
      "This is not a tax receipt -- your charitable deduction was recorded at the time of " +
      "your contribution to " +
      (gift.dafSponsoringOrg || "the sponsoring organization") +
      "."
    );
  }

  // cash / check / cc
  if (gift.fairMarketValue && gift.fairMarketValue > 0) {
    return (
      "In connection with your gift of " +
      formatCurrency(gift.amount) +
      ", " +
      'goods or services described as "' +
      (gift.goodsServicesDescription || "goods or services") +
      '" ' +
      "with a fair market value of " +
      formatCurrency(gift.fairMarketValue) +
      " were provided. " +
      "The deductible portion of your contribution is " +
      formatCurrency(gift.deductibleAmount) +
      "."
    );
  }

  return "No goods or services were provided in exchange for this contribution.";
};

// ── Gift Detail Block ─────────────────────────────────────────
const GiftDetailBlock = function (props) {
  var gift = props.gift;
  var fundName = props.fundName;

  // Cash / Check / Credit Card
  if (gift.giftType === "cash") {
    return (
      <div style={styles.section}>
        <div style={styles.sectionTitle}>Gift Detail</div>

        <div style={styles.row}>
          <span style={styles.rowLabel}>Gift Date</span>
          <span style={styles.rowValue}>{fmtDate(gift.giftDate)}</span>
        </div>

        <div style={styles.row}>
          <span style={styles.rowLabel}>Payment Method</span>
          <span style={styles.rowValue}>
            {gift.paymentMethod === "check"
              ? "Check" + (gift.checkNumber ? " #" + gift.checkNumber : "")
              : gift.paymentMethod === "credit_card"
              ? "Credit Card"
              : gift.paymentMethod === "ach"
              ? "ACH / Bank Transfer"
              : gift.paymentMethod === "paypal"
              ? "PayPal"
              : "Cash"}
          </span>
        </div>

        <div style={styles.row}>
          <span style={styles.rowLabel}>Fund Designation</span>
          <span style={styles.rowValue}>{fundName || "Where Needed Most"}</span>
        </div>

        <div style={styles.divider} />

        <div style={styles.row}>
          <span style={styles.rowLabel}>Gross Gift Amount</span>
          <span style={styles.rowValue}>{formatCurrency(gift.amount)}</span>
        </div>

        {gift.fairMarketValue > 0 && (
          <div>
            <div style={styles.row}>
              <span style={styles.rowLabel}>Goods or Services Provided</span>
              <span style={styles.rowValue}>
                {gift.goodsServicesDescription || "--"}
              </span>
            </div>
            <div style={styles.row}>
              <span style={styles.rowLabel}>
                Fair Market Value of Goods/Services
              </span>
              <span style={styles.rowValue}>
                {formatCurrency(gift.fairMarketValue)}
              </span>
            </div>
            <div style={styles.divider} />
            <div style={styles.totalRow}>
              <span>Net Deductible Amount</span>
              <span>{formatCurrency(gift.deductibleAmount)}</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  // In-Kind
  if (gift.giftType === "inkind") {
    return (
      <div style={styles.section}>
        <div style={styles.sectionTitle}>
          Gift Detail -- In-Kind Contribution
        </div>

        <div style={styles.row}>
          <span style={styles.rowLabel}>Date Received</span>
          <span style={styles.rowValue}>{fmtDate(gift.giftDate)}</span>
        </div>

        <div style={styles.row}>
          <span style={styles.rowLabel}>Description of Donated Property</span>
          <span style={styles.rowValue}>{gift.inkindDescription || "--"}</span>
        </div>

        <div style={styles.row}>
          <span style={styles.rowLabel}>Fund Designation</span>
          <span style={styles.rowValue}>{fundName || "Where Needed Most"}</span>
        </div>

        <div style={styles.divider} />
        <div style={{ fontSize: 12, color: "#666666", fontStyle: "italic" }}>
          No dollar amount is shown. Per IRS requirements, the organization may
          not value in-kind gifts. The donor is solely responsible for
          determining the fair market value for tax purposes.
        </div>
      </div>
    );
  }

  // Stock / Securities
  if (gift.giftType === "stock") {
    return (
      <div style={styles.section}>
        <div style={styles.sectionTitle}>
          Gift Detail -- Stock / Securities Transfer
        </div>

        <div style={styles.row}>
          <span style={styles.rowLabel}>Date of Transfer</span>
          <span style={styles.rowValue}>
            {fmtDate(gift.stockTransferDate || gift.giftDate)}
          </span>
        </div>

        {gift.stockTicker && (
          <div style={styles.row}>
            <span style={styles.rowLabel}>Security (Ticker)</span>
            <span style={styles.rowValue}>
              {gift.stockTicker.toUpperCase()}
            </span>
          </div>
        )}

        {gift.stockShares && (
          <div style={styles.row}>
            <span style={styles.rowLabel}>Number of Shares</span>
            <span style={styles.rowValue}>{gift.stockShares}</span>
          </div>
        )}

        <div style={styles.row}>
          <span style={styles.rowLabel}>Fund Designation</span>
          <span style={styles.rowValue}>{fundName || "Where Needed Most"}</span>
        </div>

        <div style={styles.divider} />
        <div style={{ fontSize: 12, color: "#666666", fontStyle: "italic" }}>
          No value is assigned to stock gifts per IRS requirements. The
          deductible amount is determined by the donor based on the fair market
          value on the date of transfer.
        </div>
      </div>
    );
  }

  // Donor Advised Fund
  if (gift.giftType === "daf") {
    return (
      <div style={styles.section}>
        <div style={styles.sectionTitle}>
          Gift Detail -- Donor Advised Fund Grant
        </div>

        <div style={styles.row}>
          <span style={styles.rowLabel}>Grant Date</span>
          <span style={styles.rowValue}>{fmtDate(gift.giftDate)}</span>
        </div>

        <div style={styles.row}>
          <span style={styles.rowLabel}>Sponsoring Organization</span>
          <span style={styles.rowValue}>{gift.dafSponsoringOrg || "--"}</span>
        </div>

        {gift.dafGrantId && (
          <div style={styles.row}>
            <span style={styles.rowLabel}>Grant Reference</span>
            <span style={styles.rowValue}>{gift.dafGrantId}</span>
          </div>
        )}

        <div style={styles.row}>
          <span style={styles.rowLabel}>Grant Amount Received</span>
          <span style={styles.rowValue}>{formatCurrency(gift.amount)}</span>
        </div>

        <div style={styles.row}>
          <span style={styles.rowLabel}>Fund Designation</span>
          <span style={styles.rowValue}>{fundName || "Where Needed Most"}</span>
        </div>
      </div>
    );
  }

  return null;
};

// ── Default text constants (used when admin field is blank) ───
const DEFAULTS = {
  thankYou:
    "Thank you for your generous contribution to {chapterName}. Your support makes our work possible.",
  thankYouDaf:
    "Thank you for directing a grant from {dafOrg} to {chapterName}. We are grateful for your generous support.",
  closing: "With gratitude,",
  dafNotice:
    "Note: This is a gift acknowledgment letter, not a tax receipt. The donor received their tax receipt from the sponsoring organization at the time of their contribution.",
};

// ── Main Component ────────────────────────────────────────────
export const GiftReceiptTemplate = function (props) {
  var gift = props.gift;
  var member = props.member;
  var chapterSettings = props.chapterSettings;
  var fundName = props.fundName;

  if (!gift || !member || !chapterSettings) {
    return (
      <div style={{ padding: 24, color: "#991b1b" }}>
        Missing required data to render receipt.
      </div>
    );
  }

  var cs = chapterSettings;
  var isDaf = gift.giftType === "daf";
  var generatedDate = new Date().toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  // Build token context for this receipt
  var ctx = buildReceiptContext(gift, member, cs);

  // Resolve custom text with token replacement, fallback to defaults
  var thankYouText = replaceTokens(
    cs.receiptThankYou || (isDaf ? DEFAULTS.thankYouDaf : DEFAULTS.thankYou),
    ctx
  );
  // For DAF, use the DAF-specific field if set, otherwise fall back
  if (isDaf && cs.receiptThankYouDaf) {
    thankYouText = replaceTokens(cs.receiptThankYouDaf, ctx);
  }
  var closingText = cs.receiptClosing || DEFAULTS.closing;
  var dafNoticeText = replaceTokens(
    cs.receiptDafNotice || DEFAULTS.dafNotice,
    ctx
  );

  // Donor display name
  var donorName = ctx.donorFullName;

  // Address lines (DB fields: home_street, home_city, home_state, home_zip)
  var addressLines = [];
  if (member.homeStreet) addressLines.push(member.homeStreet);
  var cityStateZip = [member.homeCity, member.homeState, member.homeZip]
    .filter(Boolean)
    .join(", ");
  if (cityStateZip) addressLines.push(cityStateZip);

  return (
    <div>
      {/* Inject print styles */}
      <style>{PRINT_STYLES}</style>

      <div className="gift-receipt-printable" style={styles.page}>
        {/* ── Header ──────────────────────────────────────── */}
        <div style={styles.header}>
          {cs.logoUrl && (
            <div style={styles.logoBlock}>
              <img src={cs.logoUrl} alt="Chapter logo" style={styles.logo} />
            </div>
          )}

          <div style={styles.orgBlock}>
            <div style={styles.orgName}>{cs.legalName || "Chapter Name"}</div>
            <div style={styles.orgMeta}>
              {cs.addressLine1 && <div>{cs.addressLine1}</div>}
              {cs.addressLine2 && <div>{cs.addressLine2}</div>}
              {cs.cityStateZip && <div>{cs.cityStateZip}</div>}
              {cs.phone && <div>{cs.phone}</div>}
              {cs.email && <div>{cs.email}</div>}
              {cs.ein && (
                <div style={{ marginTop: 4, fontWeight: 600 }}>
                  EIN: {cs.ein}
                </div>
              )}
            </div>
          </div>

          <div style={styles.receiptBadge}>
            <div style={styles.receiptLabel}>
              {isDaf ? "Acknowledgment" : "Receipt"}
            </div>
            <div style={styles.receiptNumber}>
              {gift.receiptNumber || "PENDING"}
            </div>
            <div style={styles.receiptDate}>{"Issued: " + generatedDate}</div>
            <div style={styles.receiptDate}>
              {"Gift Date: " + fmtDate(gift.giftDate)}
            </div>
          </div>
        </div>

        {/* ── DAF notice ──────────────────────────────────── */}
        {isDaf && <div style={styles.dafNote}>{dafNoticeText}</div>}

        {/* ── Salutation ──────────────────────────────────── */}
        <div style={styles.salutation}>
          <div style={{ marginBottom: 0 }}>
            <span style={styles.donorName}>{donorName}</span>
          </div>
          {addressLines.map(function (line, i) {
            return (
              <div key={i} style={{ fontSize: 13, color: "#333333" }}>
                {line}
              </div>
            );
          })}
          {addressLines.length > 0 && <div style={{ marginBottom: 16 }} />}

          <p style={{ margin: "16px 0 0 0" }}>
            {"Dear " + (member.firstName || "Friend") + ","}
          </p>

          <p style={{ margin: "12px 0" }}>{thankYouText}</p>
        </div>

        {/* ── Gift Detail Block (conditional by type) ─────── */}
        <GiftDetailBlock gift={gift} fundName={fundName} />

        {/* ── IRS Acknowledgment (NOT editable) ───────────── */}
        <div style={styles.irsBlock}>{getIrsLanguage(gift, cs.legalName)}</div>

        {/* ── Closing ─────────────────────────────────────── */}
        <p style={{ margin: "0 0 32px 0" }}>{closingText}</p>

        {/* ── Signature Block ──────────────────────────────── */}
        <div style={styles.signatureBlock}>
          {cs.signatureUrl ? (
            <img
              src={cs.signatureUrl}
              alt="Signature"
              style={styles.signatureImg}
            />
          ) : (
            <div style={styles.signatureLine} />
          )}
          {cs.signatureName && (
            <div style={styles.signatureName}>{cs.signatureName}</div>
          )}
          {cs.signatureTitle && (
            <div style={styles.signatureTitle}>{cs.signatureTitle}</div>
          )}
          <div style={styles.signatureTitle}>{cs.legalName || ""}</div>
        </div>

        {/* ── Footer ──────────────────────────────────────── */}
        <div style={styles.footer}>
          {cs.legalName || ""}
          {cs.ein ? " | EIN: " + cs.ein : ""}
          {cs.fiscalYearNote
            ? " | " + cs.fiscalYearNote
            : " | Please retain this letter for your tax records."}
        </div>
      </div>
    </div>
  );
};
